import { useState, useEffect, useMemo, useCallback } from "react";
import { useAnalytics } from "context/AnalyticsContext";
import { rdService } from "services/rdService";

// Re-export so DashboardNavbar can import the vocabulary from one source of truth

// ── Condition category vocabulary ─────────────────────────────────────────────
// Maps the five filter keys to the condition strings stored in each trial's
// conditions[] array. Matching is case-insensitive and substring-based.

// Broad anchor substrings used for client-side filtering.
// Each term is matched case-insensitively as a substring against the conditions[] array
// stored in each trial, mirroring the anchor terms used by the backend scraper's query.cond calls.
export const CONDITION_CATEGORIES = {
  ALL:                         null,
  CEREBRAL_ANEURYSM:           ["aneurysm", "subarachnoid"],
  ISCHEMIC_STROKE:             ["ischemic stroke", "large vessel"],
  HEMORRHAGIC_STROKE:          ["hemorrhag", "intracerebral"],
  CAROTID_ARTERY_DISEASE:      ["carotid"],
  NEUROVASCULAR_MALFORMATIONS: ["arteriovenous", "malformation", "fistula"],
};

export const CONDITION_CATEGORY_LABELS = {
  ALL:                         "All Conditions",
  CEREBRAL_ANEURYSM:           "Aneurysm",
  ISCHEMIC_STROKE:             "Ischemic Stroke",
  HEMORRHAGIC_STROKE:          "Hemorrhagic Stroke",
  CAROTID_ARTERY_DISEASE:      "Carotid Disease",
  NEUROVASCULAR_MALFORMATIONS: "Vascular Malformations",
};

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useRDLandscapeData() {
  // rdCompetitorId and rdConditionFilter both live in AnalyticsContext so the
  // DashboardNavbar selectors and this hook share the same state instances.
  const { rdCompetitorId, rdConditionFilter } = useAnalytics();

  const [allTrials, setAllTrials] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const fetchTrials = useCallback(() => {
    setLoading(true);
    setError(null);

    rdService
      .getTrials(rdCompetitorId)
      .then((res) => setAllTrials(res.data || []))
      .catch((err) => {
        console.error("Error fetching clinical trials:", err);
        setError("Failed to load clinical trial data");
        setAllTrials([]);
      })
      .finally(() => setLoading(false));
  }, [rdCompetitorId]);

  useEffect(() => {
    fetchTrials();
  }, [fetchTrials]);

  // Client-side condition filtering — instant, no additional API call
  const trials = useMemo(() => {
    const terms = CONDITION_CATEGORIES[rdConditionFilter];
    if (!terms) return allTrials;

    return allTrials.filter((trial) =>
      Array.isArray(trial.conditions) &&
      trial.conditions.some((condition) =>
        terms.some((term) =>
          condition.toLowerCase().includes(term.toLowerCase())
        )
      )
    );
  }, [allTrials, rdConditionFilter]);

  return {
    trials,
    allTrials,
    loading,
    error,
  };
}
